<template>
  <main class="container">
    <div class="page-header">
      <BackButton class="back-link back-link-left-align" />
      <h1
        class="page-title-mobile page-title"
        data-testid="page-title"
        v-if="!errorInBankDetails"
      >
        Set up your Direct Debit
      </h1>
      <h1 class="page-title" v-else>
        Whoops! Something's not right
      </h1>
    </div>

    <div class="layout">
      <div class="block block-1">
        <WarningPanel
          v-if="errorInBankDetails"
          :msg="bankDetailsErrorMessage"
          :severe="true"
        />

        <InfoBoxBankDetailsTest>
          <template v-slot>
            <p>
              Add your Direct Debit details below so you can pay
              <strong> your share of the bills</strong> from your own bank
              account.
            </p>
            <p>
              Your first payment will be on or just after your utilities start
              date of
              <strong data-testid="start-date"
                >{{ tenancyStartDate | FormatDate }} and you will only pay your
                share.
              </strong>
            </p>
          </template>
        </InfoBoxBankDetailsTest>
        <div id="details-test" class="container-border container-border-mobile">
          <div class="form-title">Payment Account Information</div>
          <form
            class="form personal-form"
            novalidate
            @submit.prevent="formSubmit"
            autocomplete="on"
          >
            <div id="details-test">
              <fieldset
                id="holder-name-test"
                class="form__fieldset form__fieldset--responsive"
              >
                <div
                  class="form-item-flex"
                  :class="{ '--has-error': $v.firstname.$error }"
                >
                  <label class="form__label" for="fname">
                    <span class="form__label-text"
                      >Account holder’s first name</span
                    >
                    <FormError
                      v-if="$v.firstname.$error && !$v.firstname.required"
                      msg="First name required"
                      data-testid="first-name-error"
                    />
                  </label>
                  <input
                    class="form__input"
                    type="text"
                    id="fname"
                    data-testid="first-name"
                    name="fname"
                    autocomplete="given-name"
                    v-model.trim="firstname"
                  />
                </div>

                <div
                  class="form-item-flex"
                  :class="{ '--has-error': $v.lastname.$error }"
                >
                  <label class="form__label" for="lname">
                    <span class="form__label-text"
                      >Account holder’s last name</span
                    >
                    <FormError
                      v-if="$v.lastname.$error && !$v.lastname.required"
                      msg="Last name required"
                      data-testid="last-name-error"
                    />
                  </label>
                  <input
                    class="form__input"
                    type="text"
                    id="lname"
                    data-testid="last-name"
                    name="lname"
                    required
                    autocomplete="family-name"
                    v-model.trim="lastname"
                  />
                </div>
              </fieldset>

              <fieldset class="form__fieldset form__fieldset--responsive">
                <div
                  class="form-item-flex"
                  :class="{ '--has-error': $v.sortCodeNumeric.$error }"
                >
                  <label class="form__label" for="sortCode">
                    <span class="form__label-text">Sort code</span>
                  </label>
                  <div>
                    <FormError
                      v-if="
                        $v.sortCodeNumeric.$error &&
                          !$v.sortCodeNumeric.required
                      "
                      msg="Sort code required"
                      data-testid="sort-code-required-error"
                    />
                    <FormError
                      v-if="
                        $v.sortCodeNumeric.$error &&
                          (!$v.sortCodeNumeric.minLength ||
                            !$v.sortCodeNumeric.maxLength)
                      "
                      msg="Sort code must be 6 digits long"
                      data-testid="sort-code-length-error"
                    />
                    <FormError
                      v-if="
                        $v.sortCodeNumeric.$error && !$v.sortCodeNumeric.numeric
                      "
                      msg="Sort code must only contain numeric digits"
                      data-testid="sort-code-format-error"
                    />
                  </div>
                  <input
                    class="form__input"
                    :class="{ '--has-error': $v.sortCodeNumeric.$error }"
                    type="tel"
                    id="sortCode"
                    data-testid="sort-code"
                    name="sortCode"
                    required
                    autocomplete="sortCode"
                    v-model.trim="sortCode"
                    v-mask="'##-##-##'"
                    @input="bankDetailsChanged"
                  />
                </div>
                <div
                  class="form-item-flex"
                  :class="{ '--has-error': $v.accountNumber.$error }"
                >
                  <label class="form__label" for="accountNumber">
                    <span class="form__label-text">Account number</span>
                  </label>
                  <div>
                    <FormError
                      v-if="
                        $v.accountNumber.$error && !$v.accountNumber.required
                      "
                      msg="Account number required"
                      data-testid="account-number-error"
                    />
                    <FormError
                      v-if="
                        $v.accountNumber.$error &&
                          (!$v.accountNumber.minLength ||
                            !$v.accountNumber.maxLength)
                      "
                      data-testid="account-number-length-error"
                      msg="Account number must be 8 digits long"
                    />
                    <FormError
                      v-if="
                        $v.accountNumber.$error && !$v.accountNumber.numeric
                      "
                      msg="Account number must only contain numeric digits"
                      data-testid="account-number-format-error"
                    />
                  </div>
                  <div class="bank-details__account--wrapper">
                    <input
                      class="form__input"
                      :class="{ '--has-error': $v.accountNumber.$error }"
                      type="tel"
                      id="accountNumber"
                      data-testid="account-number"
                      name="accountNumber"
                      required
                      autocomplete="accountNumber"
                      v-model.trim="accountNumber"
                      v-mask="'########'"
                      @input="bankDetailsChanged"
                    />
                    <div class="bank-icon" v-if="bankIconPath">
                      <img :src="bankIconPath" />
                    </div>
                  </div>
                </div>
              </fieldset>
            </div>

            <div class="content-panel package-terms">
              <fieldset class="form__fieldset">
                <div
                  class="form-item bank-details__confirm"
                  :class="{ '--has-error': $v.accountHolder.$error }"
                >
                  <div class="form-checkbox">
                    <FormError
                      v-if="$v.accountHolder.$error && !$v.accountHolder.sameAs"
                      msg="You must confirm that you are the account holder"
                      data-testid="account-holder-confirmation-error"
                    />
                    <input
                      class="form-checkbox__input"
                      type="checkbox"
                      name="accountHolder"
                      data-testid="account-holder-confirmation-input"
                      id="accountHolder"
                      v-model="accountHolder"
                    />
                    <label
                      for="accountHolder"
                      class="form-checkbox__label"
                      data-testid="account-holder-confirmation"
                      >I’m the account holder and authorise a Direct Debit to be
                      set up for this account.</label
                    >
                  </div>
                </div>
              </fieldset>
            </div>

            <div class="dd-submit__wrapper" ref="nextButtonWrapper">
              <div class="dd-submit__inner">
                <p>
                  By placing this order, you agree to our
                  <a
                    href="https://www.unihomes.co.uk/ums-terms-and-conditions"
                    target="_blank"
                    >Terms and Conditions</a
                  >. See how your data is used in our
                  <a
                    href="https://www.unihomes.co.uk/privacy-and-cookie-policies"
                    target="_blank"
                    >Privacy Policy</a
                  >.
                </p>
                <button
                  id="bank-details-submit-button"
                  class="btn btn--big btn--full-width"
                  type="submit"
                  @click="trackSubmitBtn"
                  data-testid="next-button"
                >
                  Confirm
                </button>
              </div>
            </div>

            <div
              class="dd-submit__wrapper dd-submit__wrapper--shadow dd-submit__wrapper--sticky"
              ref="nextButtonWrapperSticky"
              v-show="isSticky"
            >
              <div class="dd-submit__inner">
                <p>
                  By placing this order, you agree to our
                  <a
                    href="https://www.unihomes.co.uk/ums-terms-and-conditions"
                    target="_blank"
                    >Terms and Conditions</a
                  >. See how your data is used in our
                  <a
                    href="https://www.unihomes.co.uk/privacy-and-cookie-policies"
                    target="_blank"
                    >Privacy Policy</a
                  >.
                </p>
                <button
                  id="bank-details-submit-button-sticky"
                  class="btn btn--big btn--full-width"
                  type="submit"
                  @click="trackSubmitBtn"
                  data-testid="next-button"
                >
                  Confirm
                </button>
              </div>
            </div>
          </form>

          <div class="dd-guarantee">
            <div class="dd-guarantee__body">
              <p>
                Your payments are protected by the
                <button
                  class="link-button"
                  @click="ddGuaranteePanelShowing = true"
                >
                  <strong>Direct Debit Guarantee</strong></button
                >.<br />
                Payments by GoCardless. Read the
                <a href="https://gocardless.com/privacy" target="_blank">
                  <strong>privacy notice</strong></a
                >.
              </p>

              <p>
                UniHomes<br />
                6th Floor<br />
                1 New Era Square<br />
                Sheffield<br />
                S2 4RB<br />
                0330 053 9351
              </p>
            </div>

            <DirectDebitLogo width="120px" height="39px" />
          </div>
        </div>
      </div>
      <quote-summary class="block block-2" />
      <!--      <div class="block block-2">Block 2</div>-->
    </div>
    <ProductInfoOverlay
      v-if="ddGuaranteePanelShowing"
      title="Direct Debit Guarantee"
      @close-info-panel="ddGuaranteePanelShowing = false"
    >
      <template #info>
        <div class="dd-guarantee-text">
          This Guarantee is offered by all banks and building societies that
          accept instructions to pay Direct Debits.

          <ul>
            <li>
              If there are any changes to the amount, date or frequency of your
              Direct Debit, UniHomes will notify you 5 working days in advance
              of your account being debited or as otherwise agreed. If you
              request UniHomes to collect a payment, confirmation of the amount
              and date will be given to you at the time of the request.
            </li>
            <li>
              If an error is made in the payment of your Direct Debit, by
              UniHomes or your bank or building society, you are entitled to a
              full and immediate refund of the amount paid from your bank or
              building society.
            </li>
            <li>
              If you receive a refund you are not entitled to, you must pay it
              back when UniHomes asks you to.
            </li>
            <li>
              You can cancel a Direct Debit at any time by simply contacting
              your bank or building society. Written confirmation may be
              required. Please also notify us.
            </li>
          </ul>
        </div>
      </template>
    </ProductInfoOverlay>
  </main>
</template>

<script>
import {
  maxLength,
  minLength,
  numeric,
  required,
  sameAs,
} from "vuelidate/lib/validators";
import { mapGetters, mapState } from "vuex";
import { mask } from "vue-the-mask";
import debounce from "lodash.debounce";
import getBankIcon from "@/utility/bankIcon";
import FormError from "@/components/forms/FormError.vue";
import FormatDate from "@/filters/FormatDate";
import DirectDebitLogo from "@/assets/svg/logo-direct-debit.svg";
import WarningPanel from "@/components/WarningPanel";
import axiosInstance from "@/utility/axiosInstance";
import QuoteSummary from "@/components/QuoteSummary.vue";
import InfoBoxBankDetailsTest from "@/components/InfoBoxBankDetailsTest.vue";
import ProductInfoOverlay from "@/components/ProductInfoOverlay.vue";

export default {
  name: "BankDetails",
  data() {
    return {
      bankDetailsError: false,
      bankDetailsErrorMessage:
        "Please check the details below are right and that you’re using a bank account that accepts direct debits. If you’re still having trouble, please call on 0330 053 9351.",
      bankIconPath: null,
      isSticky: false,
      ddGuaranteePanelShowing: false,
      errorInBankDetails: false,
    };
  },
  computed: {
    ...mapState(["bankDetails"]),
    ...mapGetters([
      "isLeadTenant",
      "tenancyStartDate",
      "hasBroadband",
      "hasUsedDiscount",
    ]),
    firstname: {
      get() {
        return this.bankDetails.firstname;
      },
      set(value) {
        this.$store.commit("SET_BANK_DETAILS_PROP", { firstname: value });
      },
    },
    lastname: {
      get() {
        return this.bankDetails.lastname;
      },
      set(value) {
        this.$store.commit("SET_BANK_DETAILS_PROP", { lastname: value });
      },
    },
    sortCode: {
      get() {
        return this.bankDetails.sortCode;
      },
      set(value) {
        this.$store.commit("SET_BANK_DETAILS_PROP", { sortCode: value });
      },
    },
    sortCodeNumeric() {
      return this.sortCode.replace(/-/g, "");
    },
    accountNumber: {
      get() {
        return this.bankDetails.accountNumber;
      },
      set(value) {
        this.$store.commit("SET_BANK_DETAILS_PROP", { accountNumber: value });
      },
    },
    accountHolder: {
      get() {
        return this.bankDetails.accountHolder;
      },
      set(value) {
        this.$store.commit("SET_BANK_DETAILS_PROP", { accountHolder: value });
      },
    },
    termsAgreed: {
      get() {
        return this.$store.state.personalDetails.termsAgreed;
      },
      set(value) {
        this.$store.commit("SET_PERSONAL_DETAILS_PROP", {
          termsAgreed: value,
        });
      },
    },
    isLeadTenant() {
      return this.$store.getters.isLeadTenant;
    },
    bankDetails() {
      return this.$store.state.bankDetails;
    },
    accountInformation() {
      return this.$store.state.accountInformation;
    },
    postData() {
      const guid = this.accountInformation.guid;

      let data = {
        personalDetails: this.$store.state.personalDetails,
        bankDetails: {
          ...this.bankDetails,
          firstPaymentDate: this.tenancyStartDate,
        },
        addressId: this.$store.state.addressId,
      };

      if (guid) {
        data.guid = guid;
      } else {
        data = {
          ...data,
          housemateCount: this.$store.state.housemateCount,
          houseInformation: this.$store.state.houseInformation,
          selectedProducts: this.$store.getters.selectedProductPriceIds,
        };

        if (this.$store.state.leadId) {
          data.houseInformation.leadId = this.$store.state.leadId;
        }

        if (!data.houseInformation.broadbandDate) {
          data.houseInformation.broadbandDate = data.houseInformation.startDate;
        }

        if (data.houseInformation.address.guid) {
          data.houseInformation.address = {
            guid: data.houseInformation.address.guid,
            line1: data.houseInformation.address.line1,
            ...(data.houseInformation.address.line2 && {
              line2: data.houseInformation.address.line2,
            }),
            ...(data.houseInformation.address.organisationName && {
              organisationName: data.houseInformation.address.organisationName,
            }),
            postcode: data.houseInformation.address.postcode,
          };
        }

        // if there is no broadband selected, remove the properties from payload
        if (!this.hasBroadband) {
          delete data.houseInformation.broadbandDate;
          delete data.houseInformation.broadbandUrgent;
        }

        // bolt on the referral code if it exists
        if (this.$store.state.referral.isValid) {
          data.houseInformation.referralCode = this.$store.state.referral.code;
        }

        // bolt on the affiliate code if it exists
        if (this.$store.state.affiliate.isValid) {
          data.houseInformation.affiliateCode = this.$store.state.affiliate.code;
        }

        if (this.$store.getters.hasUsedDiscount) {
          data.houseInformation.agentCode = this.$store.state.agent.code;
        }

        if (this.$store.state.discount.isValid) {
          data.houseInformation.discountCode = this.$store.state.discount.code;
        }
      }

      return data;
    },
  },
  mounted() {
    window.addEventListener("scroll", this.toggleStickyShadow);
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.toggleStickyShadow);
  },
  methods: {
    formSubmit() {
      this.termsAgreed = true;

      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.postDirectDebit();
      }
    },
    trackSubmitBtn() {
      this.trackGoogleEvent(
        "Click Set Me Up",
        this.isLeadTenant ? "Create Account" : "Joining Housemate",
        "Enter direct debit",
      );
    },
    toggleStickyShadow() {
      // hide sticky button if it's bellow form button
      const formButton = this.$refs.nextButtonWrapper;
      const formButtonRect = formButton.getBoundingClientRect();
      const windowHeight =
        window.innerHeight || document.documentElement.clientHeight;

      this.isSticky = !(formButtonRect.bottom < windowHeight);

      // hide sticky button if at the top of the page and slide it up as user scrolls down
      const stickyButton = this.$refs.nextButtonWrapperSticky;
      const stickyButtonRect = stickyButton.getBoundingClientRect();
      const distanceFromTop = window.scrollY;

      if (distanceFromTop < stickyButtonRect.height) {
        stickyButton.style.bottom =
          distanceFromTop - stickyButtonRect.height + "px";
      } else {
        stickyButton.style.bottom =
          stickyButtonRect.height != 0 ? 0 : stickyButton.style.bottom;
      }
    },
    bankDetailsChanged: debounce(function() {
      if (this.sortCode.length === 8 && this.accountNumber.length === 8) {
        axiosInstance
          .post("/bank_lookup", {
            sortCode: this.sortCode,
            accountNumber: this.accountNumber,
          })
          .then(({ data }) => {
            this.bankIconPath = getBankIcon(data.bankName);
          })
          .catch(() => {
            this.bankIconPath = null;
          });
      } else {
        this.bankIconPath = null;
      }
    }, 300),
    postDirectDebit() {
      this.$store.commit("SHOW_LOADING_PANEL");

      axiosInstance({
        method: "post",
        url: "/direct_debit",
        timeout: 20000,
        data: this.postData,
      })
        .then(({ data }) => {
          // remove any ref/aff codes from local storage
          localStorage.removeItem("referralCode");
          localStorage.removeItem("affiliateCode");
          localStorage.removeItem("agentCode");

          if (data.remainingHm8s > 0) {
            this.$store.commit("SET_ACCOUNT_PROP", {
              guid: data.guid,
              joinLink: data.joinLink,
              remainingHousemates: data.remainingHm8s,
            });
            this.$router.push("./congratulations");
          } else {
            this.$router.push({ name: "house-complete" });
          }
        })
        .catch((error) => {
          if (!error.response) {
            this.errorResponse =
              "Network error accessing the Split The Bills API";
          } else {
            // Need to redirect back to the bank details page with an error
            this.errorInBankDetails = true;
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
        })
        .then(() => {
          this.$store.commit("HIDE_LOADING_PANEL");
        });
    },
  },
  created() {
    // We already have first and last name, so lets pre-populate
    // these fields in the store if they haven't already been set
    if (!this.firstname) {
      this.firstname = this.$store.state.personalDetails.firstname;
    }
    if (!this.lastname) {
      this.lastname = this.$store.state.personalDetails.lastname;
    }
  },
  filters: {
    FormatDate,
  },
  directives: { mask },
  validations() {
    return {
      firstname: {
        required,
      },
      lastname: {
        required,
      },
      sortCodeNumeric: {
        required,
        numeric,
        minLength: minLength(6),
        maxLength: maxLength(6),
      },
      accountNumber: {
        required,
        numeric,
        minLength: minLength(8),
        maxLength: maxLength(8),
      },
      accountHolder: {
        sameAs: sameAs(() => true),
      },
      termsAgreed: {
        sameAs: sameAs(() => true),
      },
    };
  },
  components: {
    ProductInfoOverlay,
    InfoBoxBankDetailsTest,
    QuoteSummary,
    FormError,
    DirectDebitLogo,
    WarningPanel,
  },
};
</script>

<style lang="scss" scoped>
.page-title-mobile {
  @include media("<medium") {
    color: $brand-blue;
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: 150%; /* 30px */
    text-align: left;
    width: auto; // in case it's constrained
  }
}
.container {
  max-width: 120rem;
}

h1 {
  small {
    font-size: 2rem;
    font-weight: $font-regular;
  }
}

.infobox {
  margin-top: 0;
}

.content-panel {
  font-size: 1.6rem;
  margin: 3rem 0;

  @include media("<medium") {
    margin: 3rem 0;
  }

  @include media(">=medium") {
    margin: 0 0 3rem;
  }
}

.bank-details-grid-control {
  display: grid;
  grid-template-columns: 34% 60%;
  grid-template-rows: repeat(3, auto);
  grid-column-gap: 6%;
  grid-auto-flow: column;

  @include media("<medium") {
    grid-auto-flow: row;
    grid-template-columns: repeat(1, 1fr);

    input {
      margin-bottom: 22px;
    }

    .bank-icon {
      top: calc(50% - 8px);
    }
  }

  input {
    margin-top: 0.5rem;

    &.--has-error {
      border-color: $error;
    }
  }
}

.bank-details-grid-test {
  display: grid;
  grid-template-rows: repeat(3, auto);
  grid-column-gap: 6%;
  grid-auto-flow: column;

  @include media("<medium") {
    grid-auto-flow: row;
    grid-template-columns: repeat(1, 1fr);

    input {
      margin-bottom: 22px;
    }

    .bank-icon {
      top: calc(50% - 8px);
    }
  }

  input {
    margin-top: 0.5rem;

    &.--has-error {
      border-color: $error;
    }
  }
}

.bank-details__account--wrapper {
  position: relative;
}

.bank-icon {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);

  img {
    height: 15px;
  }
}

.bank-details__confirm {
  margin: $spacing-small 0;
}

.dd-guarantee {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-top: $spacing-small;

  .dd-guarantee__body {
    font-size: 1.4rem;

    p {
      margin-top: 0;
    }
  }

  svg {
    flex-shrink: 0;

    @include media("<medium") {
      width: 9rem;
      height: 3rem;
      margin-left: $spacing-medium;
    }
    @include media(">=medium") {
      margin-left: $spacing-large;
    }
  }
}

.package-terms {
  margin-top: 0;
}

.dd-submit {
  &__wrapper {
    font-size: 1.2rem;

    @include media("<medium") {
      left: 0;
      right: 0;
      bottom: env(safe-area-inset-bottom, 0);
      padding-bottom: 1rem;
      display: flex;
      flex-direction: column;
      margin-left: calc(-0.5 * 100vw + 50%);
      width: 100vw;
      background-color: white;

      &--shadow {
        box-shadow: 0 -7px 7px -4px #dadada;
      }

      &--sticky {
        position: fixed;
        bottom: -10000px;
        z-index: 1000;
      }
    }
  }

  &__inner {
    max-width: 85vw;
    margin: 0 auto;

    button {
      max-width: 26.8rem;
    }
  }
}

.form__fieldset--responsive {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @media (min-width: 1024px) {
    flex-direction: row;
  }
}

.form-item-flex {
  flex: 1 1 0;
}

.container-border {
  display: flex;
  padding: 24px;
  flex-direction: column;
  gap: 24px;
  align-self: stretch;
  border-radius: 16px;
  background: #fff;

  @media (min-width: 767px) {
    border: 1px solid var(--Mono-Grey-200, #e5e7eb);
  }
  @media (max-width: 767px) {
    border-top: 1px solid var(--Mono-Grey-200, #e5e7eb);
  }
}

.container-border-mobile {
  @media (max-width: 767px) {
    border-top: 1px solid var(--Mono-Grey-200, #e5e7eb);
    border-radius: 0px;
  }
}

.flex-title {
  display: flex;
  align-items: center;
  justify-content: center; /* centres the rest */
  gap: 1rem; /* optional spacing */
}

.back-link-left-align {
  position: absolute;
  left: 0;
  margin-bottom: 14px;
}

.page-header {
  display: flex;
  align-items: center; /* Aligns vertically */
  justify-content: center; /* Centres the title */
  position: relative;
  @include media("<medium") {
    justify-content: flex-start;
  }
}

.layout {
  display: flex;
  gap: 24px;
  flex-direction: column; // stack by default (mobile)
}

.block {
  width: 100%;
}

.block-1 {
  order: 2;
}

.block-2 {
  order: 1;
}

@media (min-width: 768px) {
  .layout {
    flex-direction: row; // switch to side-by-side
  }

  .block-1 {
    order: 1;
    flex: 2;
  }

  .block-2 {
    order: 2;
    flex: 1;
  }
}

.form-title {
  color: $brand-blue;
  font-size: $quote-subtitle-font-size;
  font-weight: $font-bold;
}

.text-bold {
  font-weight: 700;
}
</style>
